import { Button, Text } from '@noorden-insurtech/alba-design-system';
import { Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCapitalGlobalReferenceData } from '@/features/quotes/hooks/capital-global/use-capital-global-step1-reference-data.hook';
import { useCaepfSelection } from '@/features/quotes/hooks/common/use-caepf-selection.hook';
import { getCapitalGlobalDocumentLiveError } from '@/features/quotes/utils/capital-global-document-error.util';
import { removeFormatRegisterNumber } from '@/shared/utils/remove-format-register-number';
import type {
  DiretrixDocumentLookupState,
  SubStipulantEntry,
} from '../../types';
import type { SubStipulantFieldErrors } from '../utils/validate-risk.util';
import { SelectCaepfModal } from '../../../select-caepf.modal';
import { CnaeField } from './cnae-field.component';
import { DiretrixDocumentWatcher } from './diretrix-document-watcher.component';
import { SegmentSelect } from './segment-select.component';
import { StipulantIdentification } from './stipulant-identification.component';

function pickSubError(
  message: string | undefined,
  submitAttempted: boolean
): string | undefined {
  if (!submitAttempted || !message) return undefined;
  return message;
}

type Props = {
  item: SubStipulantEntry;
  onPatch(partial: Partial<SubStipulantEntry>): void;
  disabled?: boolean;
  segmentOptions?: Array<{ value: string; label: string }>;
  isSegmentOptionsLoading?: boolean;
  isSegmentOptionsError?: boolean;
  submitAttempted: boolean;
  fieldErrors?: SubStipulantFieldErrors;
  isLast: boolean;
  onAddAnother(): void;
  onRemove(): void;
  onDocumentLookupChange(state: DiretrixDocumentLookupState): void;
};

export function SubStipulantRow({
  item,
  onPatch,
  disabled = false,
  segmentOptions = [],
  isSegmentOptionsLoading,
  isSegmentOptionsError,
  submitAttempted,
  fieldErrors,
  isLast,
  onAddAnother,
  onRemove,
  onDocumentLookupChange,
}: Props) {
  const useIndividualDocument = Boolean(item.useIndividualDocument);
  const [documentHelperText, setDocumentHelperText] = useState<
    string | undefined
  >();
  const [documentLookup, setDocumentLookup] =
    useState<DiretrixDocumentLookupState>({
      cleanDocument: '',
      status: 'idle',
    });
  const [segmentLabel, setSegmentLabel] = useState('');
  const [cnaeSearch, setCnaeSearch] = useState('');
  const [cnaeLabel, setCnaeLabel] = useState('');
  const primaryActivityCode = !useIndividualDocument
    ? (documentLookup.primaryActivityCode ?? '').trim()
    : '';
  const {
    options: cnaeOptions,
    isLoading: isCnaeOptionsLoading,
    isError: isCnaeOptionsError,
  } = useCapitalGlobalReferenceData({
    tableName: 'CNAE',
    search: useIndividualDocument ? cnaeSearch : primaryActivityCode,
    pageSize: 50,
    enabled: useIndividualDocument || Boolean(primaryActivityCode),
  });
  const pendingLookupPatchRef = useRef<Partial<SubStipulantEntry>>({});
  const lookupPatchFlushScheduledRef = useRef(false);

  const queueLookupPatch = useCallback(
    (partial: Partial<SubStipulantEntry>) => {
      pendingLookupPatchRef.current = {
        ...pendingLookupPatchRef.current,
        ...partial,
      };

      if (lookupPatchFlushScheduledRef.current) return;

      lookupPatchFlushScheduledRef.current = true;
      queueMicrotask(() => {
        lookupPatchFlushScheduledRef.current = false;
        const merged = pendingLookupPatchRef.current;
        pendingLookupPatchRef.current = {};

        if (Object.keys(merged).length > 0) {
          onPatch(merged);
        }
      });
    },
    [onPatch]
  );

  const handleAverageAgesChange = useCallback(
    (ages: {
      averagePartnerAge?: number;
      averageEmployeeAge?: number;
      partnerCount?: number;
      employeeCount?: number;
    }) => queueLookupPatch(ages),
    [queueLookupPatch]
  );

  // Modal de seleção de CAEPF — mesmo comportamento do estipulante: o CPF é o
  // ponto de entrada e o documento gravado é a inscrição do CAEPF escolhido.
  const caepfSelection = useCaepfSelection({
    onSelect: (entry, consultedCpf) =>
      onPatch({
        // Guarda o CPF consultado à parte (o CAEPF sobrescreve o documentNumber).
        cpf: consultedCpf ?? '',
        ...(entry.registrationNumber
          ? { documentNumber: entry.registrationNumber }
          : {}),
        ...(entry.responsibleName ? { name: entry.responsibleName } : {}),
      }),
  });
  const { requestSelection: requestCaepfSelection, resetFor: resetCaepfFor } =
    caepfSelection;

  useEffect(() => {
    if (useIndividualDocument) return;

    if (!primaryActivityCode) {
      if (item.cnae) onPatch({ cnae: '' });
      if (cnaeLabel) setCnaeLabel('');
      return;
    }

    const cnaeOption =
      cnaeOptions.find(option => option.value === primaryActivityCode) ??
      cnaeOptions[0];

    if (item.cnae !== primaryActivityCode) {
      onPatch({ cnae: primaryActivityCode });
    }

    setCnaeLabel(cnaeOption?.label ?? primaryActivityCode);
  }, [
    cnaeLabel,
    cnaeOptions,
    item.cnae,
    onPatch,
    primaryActivityCode,
    useIndividualDocument,
  ]);

  return (
    <div className='flex flex-col gap-4'>
      <Text className='text-base font-bold text-alba-black-500'>
        Dados do Subestipulante
      </Text>

      <DiretrixDocumentWatcher
        documentNumber={item.documentNumber}
        useIndividualDocument={useIndividualDocument}
        name={item.name}
        onNameChange={value => queueLookupPatch({ name: value })}
        onAverageAgesChange={handleAverageAgesChange}
        disabled={disabled}
        onStatusChange={state => {
          setDocumentLookup(state);
          setDocumentHelperText(state.helperText);
          onDocumentLookupChange(state);

          if (state.status === 'found' && state.caepfItems?.length) {
            requestCaepfSelection(state.cleanDocument, state.caepfItems);
          }
        }}
      />

      <StipulantIdentification
        useIndividualDocument={useIndividualDocument}
        onUseIndividualDocumentChange={checked =>
          onPatch({ useIndividualDocument: checked })
        }
        documentNumber={item.documentNumber}
        onDocumentNumberChange={value => {
          // Trocar o documento descarta o CPF consultado antigo e libera o modal.
          resetCaepfFor(removeFormatRegisterNumber(value));
          onPatch({ documentNumber: value, cpf: '' });
        }}
        documentError={
          pickSubError(fieldErrors?.documentNumber, submitAttempted) ??
          // Documento inválido acusa assim que o número completa, sem esperar
          // o primeiro "Próximo".
          getCapitalGlobalDocumentLiveError(
            item.documentNumber,
            useIndividualDocument
          ) ??
          // Situação cadastral irregular vira erro (vermelho) abaixo do campo,
          // não helper text cinza.
          (documentLookup.status === 'inactive-caepf'
            ? documentLookup.helperText
            : undefined)
        }
        name={item.name}
        onNameChange={value => onPatch({ name: value })}
        nameError={pickSubError(fieldErrors?.name, submitAttempted)}
        documentHelperText={documentHelperText}
        required
        disabled={disabled}
      />

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-10'>
        <SegmentSelect
          value={item.segment}
          onChange={(value, label) => {
            onPatch({ segment: value });
            setSegmentLabel(label ?? '');
          }}
          selectedLabel={segmentLabel}
          options={segmentOptions}
          useSelect
          isLoading={isSegmentOptionsLoading}
          disabled={disabled || isSegmentOptionsLoading || isSegmentOptionsError}
          required
          error={pickSubError(fieldErrors?.segment, submitAttempted)}
        />
        <CnaeField
          value={item.cnae}
          onChange={(value, label) => {
            onPatch({ cnae: value });
            setCnaeLabel(label ?? '');
          }}
          selectedLabel={cnaeLabel}
          options={cnaeOptions}
          useCombobox={useIndividualDocument}
          search={cnaeSearch}
          onSearchChange={setCnaeSearch}
          isLoading={isCnaeOptionsLoading}
          disabled={disabled || !useIndividualDocument || isCnaeOptionsError}
          required
          error={pickSubError(fieldErrors?.cnae, submitAttempted)}
        />
      </div>

      <div className='flex gap-4'>
        {isLast ? (
          <Button
            variant='ghost'
            size='sm'
            className='flex items-center gap-2'
            onClick={onAddAnother}
            disabled={disabled}
          >
            <Plus className='w-5 h-5' />
            Adicionar outro
          </Button>
        ) : null}
        <Button
          variant='ghost'
          size='sm'
          className='flex items-center gap-2'
          onClick={onRemove}
          disabled={disabled}
        >
          <Trash2 className='w-5 h-5' />
          Remover
        </Button>
      </div>

      <SelectCaepfModal
        isOpen={caepfSelection.isOpen}
        onClose={caepfSelection.close}
        onConfirm={caepfSelection.confirm}
        items={caepfSelection.items}
      />
    </div>
  );
}
